import React, { memo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { Box } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { selectIncompleteLinkedFields } from 'store/loanApplications/selectors';

import FieldCompletenessIndicator from '../FieldCompletenessIndicator';
import FieldLockIndicator from '../FieldLockIndicator';
import PendingChangeAuthorDots from '../PendingChangeAuthorDots';

import { authorDotsIndicatorStyles, getWrapperStyles } from './FieldPresenceWrapper.styles';
import { resolveCompletenessEntry } from './FieldPresenceWrapper.utils';
import useFieldPresenceBehavior from './hooks/useFieldPresenceBehavior';

const FieldPresenceWrapper = ({
  fieldPath = '',
  fieldId = '',
  formName = '',
  children,
  groupLock = null,
  formValue,
  columns = 1,
  wrapperStyles,
  pendingChangeAuthors = null,
  fieldLabel = '',
}) => {
  const wrapperRef = useRef(null);

  // Authored fieldRef is the full appFormsTree path; a field's fieldId is the leaf — so this
  // selector is scoped to the form and keyed by leaf (mirrors autoLock's selectFormLockedFields).
  const incompleteLinkedFields = useSelector(selectIncompleteLinkedFields(formName));

  const {
    lock,
    isLockedByAnotherUser,
    lockUserColorSet,
    onFocusField,
    onBlurField,
    onInputChange,
  } = useFieldPresenceBehavior({ fieldPath, groupLock, formValue, wrapperRef });

  const completenessEntry = resolveCompletenessEntry({
    incompleteLinkedFields,
    fieldId,
    fieldPath,
  });

  const isFieldNeeded = !!completenessEntry && !completenessEntry.isOverridden;
  const hasPendingChangeAuthors = !_.isEmpty(pendingChangeAuthors);

  const pendingChangeAuthorsIndicator = hasPendingChangeAuthors && (
    <Box sx={authorDotsIndicatorStyles}>
      <PendingChangeAuthorDots authors={pendingChangeAuthors} fieldLabel={fieldLabel} />
    </Box>
  );

  // No presence or completeness affordances — return children as-is to preserve layout.
  if (!fieldPath && !completenessEntry && !hasPendingChangeAuthors) {
    return children;
  }

  // fieldId only (no lock path) — minimal wrapper for the completeness indicator.
  if (!fieldPath) {
    return (
      <Box ref={wrapperRef} sx={getWrapperStyles({ columns, wrapperStyles, isFieldNeeded })}>
        {pendingChangeAuthorsIndicator}
        {completenessEntry && (
          <FieldCompletenessIndicator
            checklistId={completenessEntry.checklistId}
            isOverridden={completenessEntry.isOverridden}
            itemId={completenessEntry.itemId}
            targetId={completenessEntry.targetId}
            targetType={completenessEntry.targetType}
          />
        )}
        {children}
      </Box>
    );
  }

  return (
    <Box
      ref={wrapperRef}
      sx={getWrapperStyles({
        isLockedByAnotherUser,
        userColor: lockUserColorSet?.bg,
        columns,
        wrapperStyles,
        isFieldNeeded,
      })}
      onBlur={onBlurField}
      onChange={onInputChange}
      onFocus={onFocusField}
      onInput={onInputChange}
    >
      {pendingChangeAuthorsIndicator}
      {isLockedByAnotherUser && (
        <FieldLockIndicator displayName={lock?.displayName} userColor={lockUserColorSet?.fg} />
      )}
      {completenessEntry && (
        <FieldCompletenessIndicator
          checklistId={completenessEntry.checklistId}
          isOverridden={completenessEntry.isOverridden}
          itemId={completenessEntry.itemId}
          targetId={completenessEntry.targetId}
          targetType={completenessEntry.targetType}
        />
      )}
      {children}
    </Box>
  );
};

FieldPresenceWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  fieldPath: PropTypes.string,
  fieldId: PropTypes.string,
  formName: PropTypes.string,
  columns: PropTypes.number,
  wrapperStyles: PropTypes.object,
  formValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.object,
    PropTypes.array,
  ]),
  groupLock: PropTypes.object,
  pendingChangeAuthors: PropTypes.arrayOf(PropTypes.object),
  fieldLabel: PropTypes.string,
};

export default memo(FieldPresenceWrapper);
